import React, { createContext, useState, useEffect, useRef } from 'react';
import { AxiosError } from 'axios';
import { createDefaultSurvey, Survey } from 'models/survey';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useNavigate, useParams } from 'react-router-dom';
import { openNotification } from 'services/notificationService/notificationSlice';
import { SurveyParams } from 'components/admin/survey/types';
import { getEmailVerification } from 'services/emailVerificationService';
import { getEngagement } from 'services/engagementService';
import { getSurvey } from 'services/surveyService';
import { submitSurvey } from 'services/submissionService';
import { Engagement, createDefaultEngagement } from 'models/engagement';
import { getSlugByEngagementId } from 'services/engagementSlugService';
import { analyticsService } from 'services/penguinAnalytics';

interface SubmitSurveyContext {
    savedSurvey: Survey;
    isSurveyLoading: boolean;
    token?: string;
    isTokenValid: boolean;
    handleSubmit: (submissionData: unknown) => void;
    isSubmitting: boolean;
    savedEngagement: Engagement | null;
    isEngagementLoading: boolean;
    loadEngagement: null | (() => void);
    slug: string;
}

export const SubmitSurveyContext = createContext<SubmitSurveyContext>({
    savedSurvey: createDefaultSurvey(),
    isSurveyLoading: true,
    isTokenValid: true,
    handleSubmit: (_submissionData: unknown) => {
        return;
    },
    isSubmitting: false,
    savedEngagement: null,
    isEngagementLoading: true,
    loadEngagement: null,
    slug: '',
});

export const SubmitSurveyProvider = ({ children }: { children: JSX.Element }) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const isLoggedIn = useAppSelector((state) => state.user.authentication.authenticated);
    const { surveyId, token } = useParams<SurveyParams>();
    const [savedSurvey, setSavedSurvey] = useState<Survey>(createDefaultSurvey());
    const [isSurveyLoading, setIsSurveyLoading] = useState(true);
    const [isTokenValid, setTokenValid] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [savedEngagement, setSavedEngagement] = useState<Engagement | null>(null);
    const savedEngagementRef = useRef<Engagement | null>(null);
    const [isEngagementLoading, setIsEngagementLoading] = useState(true);
    const [slug, setSlug] = useState<string>('');
    const [participantId, setParticipantId] = useState<string | undefined>(() => {
        // Restore participant_id from sessionStorage on mount (handles page refresh)
        return sessionStorage.getItem(`participant_id_${surveyId}`) || undefined;
    });

    const verifyToken = async (survey?: Survey) => {
        if (isLoggedIn) {
            setIsSurveyLoading(false);
            return;
        }

        if (!token) {
            navigate(`/not-found`);
            return;
        }

        try {
            const verification = await getEmailVerification(token);
            if (!verification || verification.survey_id !== Number(surveyId)) {
                throw new Error('verification not found or does not match survey');
            }
            // Store participant_id for survey_submit tracking (persist to sessionStorage for page refresh)
            const pid = verification.participant_id?.toString();
            setParticipantId(pid);
            if (pid) {
                sessionStorage.setItem(`participant_id_${surveyId}`, pid);
            }
            // Track survey landing from email link (token links this to email_submitted event)
            const surveyForTracking = survey || savedSurvey;
            // Fetch engagement name for survey_start (engagement loads in parallel so we resolve it here)
            let engagementNameForStart: string | undefined;
            if (surveyForTracking.engagement_id) {
                try {
                    const eng = await getEngagement(Number(surveyForTracking.engagement_id));
                    engagementNameForStart = eng.name;
                    setSavedEngagement(eng);
                    savedEngagementRef.current = eng;
                } catch {
                    // non-fatal: engagement_name will just be absent
                }
            }
            analyticsService.track({
                action: 'survey_start',
                engagement_id: surveyForTracking.engagement_id?.toString() || '',
                engagement_name: engagementNameForStart,
                survey_id: surveyId,
                survey_name: surveyForTracking.name,
                verification_token: token,
                participant_id: verification.participant_id?.toString(),
            });
            // Snowplow tracking for survey start
            try {
                window.snowplow('trackSelfDescribingEvent', {
                    schema: 'iglu:ca.bc.gov.met/action/jsonschema/1-0-0',
                    data: {
                        action: 'survey_start',
                        survey_name: (survey || savedSurvey).name,
                        survey_id: surveyId,
                        participant_number: verification.participant_id,
                    },
                });
            } catch (error) {
                console.log(error);
            }
        } catch (error) {
            dispatch(
                openNotification({
                    severity: 'error',
                    text: 'Verification token is invalid.',
                }),
            );
            setTokenValid(false);
        } finally {
            setIsSurveyLoading(false);
        }
    };

    useEffect(() => {
        loadSurvey();
    }, []);
    const loadSurvey = async () => {
        if (isNaN(Number(surveyId))) {
            navigate('/not-found');
            dispatch(
                openNotification({
                    severity: 'error',
                    text: 'The survey id passed was erroneous',
                }),
            );
            return;
        }
        try {
            const loadedSurvey = await getSurvey(Number(surveyId));
            setSavedSurvey(loadedSurvey);
            setIsSurveyLoading(false);
            verifyToken(loadedSurvey);
        } catch (error) {
            if ((error as AxiosError)?.response?.status === 500) {
                navigate('/not-available');
            } else {
                dispatch(
                    openNotification({
                        severity: 'error',
                        text: 'Error occurred while loading saved survey',
                    }),
                );
                navigate(`/not-available`);
            }
        }
    };

    useEffect(() => {
        if (savedSurvey?.id !== 0) {
            loadEngagement();
        }
    }, [savedSurvey]);
    const loadEngagement = async () => {
        if (isNaN(Number(savedSurvey.engagement_id)) || !savedSurvey.engagement_id) {
            const defaultEngagement = { ...createDefaultEngagement(), name: savedSurvey.name };
            setSavedEngagement(defaultEngagement);
            savedEngagementRef.current = defaultEngagement;
            setIsEngagementLoading(false);
            return;
        }

        setIsEngagementLoading(true);
        try {
            // Skip fetch if verifyToken already loaded the engagement into the ref
            const loadedEngagement = savedEngagementRef.current?.id === Number(savedSurvey.engagement_id)
                ? savedEngagementRef.current
                : await getEngagement(Number(savedSurvey.engagement_id));
            setSavedEngagement(loadedEngagement);
            savedEngagementRef.current = loadedEngagement;
            setIsEngagementLoading(false);
        } catch (error) {
            dispatch(
                openNotification({
                    severity: 'error',
                    text: 'Error occurred while loading saved engagement data',
                }),
            );
            setIsEngagementLoading(false);
        }
    };

    const handleFetchSlug = async () => {
        if (!savedSurvey.engagement_id) {
            return;
        }
        try {
            const response = await getSlugByEngagementId(savedSurvey.engagement_id);
            setSlug(response.slug);
        } catch (error) {
            setSlug('');
        }
    };

    useEffect(() => {
        handleFetchSlug();
    }, [savedSurvey.engagement_id]);

    const handleSubmit = async (submissionData: unknown) => {
        try {
            setIsSubmitting(true);
            await submitSurvey({
                survey_id: savedSurvey.id,
                submission_json: submissionData,
                verification_token: token ? token : '',
            });

            try {
                window.snowplow('trackSelfDescribingEvent', {
                    schema: 'iglu:ca.bc.gov.met/submit-survey/jsonschema/1-0-0',
                    data: { survey_id: savedSurvey.id, engagement_id: savedSurvey.engagement_id },
                });
            } catch (error) {
                console.log(error);
            }
            // Track survey completion (get participant_id from state or sessionStorage fallback)
            const submittingParticipantId = participantId || sessionStorage.getItem(`participant_id_${surveyId}`) || undefined;
            analyticsService.track({
                action: 'survey_submit',
                engagement_id: savedSurvey.engagement_id?.toString() || '',
                engagement_name: savedEngagementRef.current?.name,
                survey_id: savedSurvey.id.toString(),
                survey_name: savedSurvey.name,
                verification_token: token || '',
                participant_id: submittingParticipantId,
            });
            // Clean up sessionStorage after successful submit
            sessionStorage.removeItem(`participant_id_${surveyId}`);
            dispatch(
                openNotification({
                    severity: 'success',
                    text: 'Survey was successfully submitted',
                }),
            );
            navigate(`/${slug}`, {
                state: {
                    open: true,
                },
            });
        } catch (error) {
            dispatch(
                openNotification({
                    severity: 'error',
                    text: 'Error occurred during survey submission',
                }),
            );
            setIsSubmitting(false);
            verifyToken();
        }
    };

    return (
        <SubmitSurveyContext.Provider
            value={{
                savedSurvey,
                isSurveyLoading,
                token,
                isTokenValid,
                handleSubmit,
                isSubmitting,
                savedEngagement,
                isEngagementLoading,
                loadEngagement,
                slug,
            }}
        >
            {children}
        </SubmitSurveyContext.Provider>
    );
};
